import { queryClient } from "@/config/react-query";

import createId from "@/lib/create-id";

import type {
  CohortDateRangeTuple,
  CohortMonthDayRange,
  CohortNumericRange,
} from "@/types/cohort";
import { UserType } from "@/types/enums/user-type";
import type { Page } from "@/types/pagination";
import type {
  PatientChart,
  PatientCreate,
  PatientFetchParams,
  PatientSummary,
  PatientTable,
  PatientTableRequest,
  PatientUpdate,
} from "@/types/patient";
import type { PatientTag } from "@/types/patient-tag";

import { patientQueryKeys } from "./patient-query";

/**
 * Build a minimal optimistic PatientSummary from a create payload.
 * Uses a temporary id. Server result will replace via invalidation.
 */
export function buildOptimisticSummaryFromCreate(
  payload: PatientCreate,
): PatientSummary {
  return {
    id: createId("usr"),
    firstName: payload.firstName,
    lastName: payload.lastName,
    phoneNumber: payload.phoneNumber,
    email: payload.email,
    creditBalance: 0,
    createdDate: new Date().toISOString(),
    roles: [],
    type: UserType.PATIENT,
  } as PatientSummary;
}

/**
 * Convert a PatientSummary to a PatientTable shape with safe defaults.
 * PatientTable extends PatientDetail with non-null totals for sorting.
 */
export function summaryToTable(summary: PatientSummary): PatientTable {
  return {
    ...summary,
    measurements: [],
    medications: [],
    appointmentCount: 0,
    totalSpent: 0,
  } as PatientTable;
}

type Snapshot = [readonly unknown[], unknown];

type CachedPatient = PatientSummary | PatientChart | PatientTable;

type PatientCacheUpdate =
  | PatientUpdate
  | (Omit<Partial<CachedPatient>, "profilePictureUrl" | "profileImageUrl"> & {
      medications?: PatientTable["medications"];
      profilePictureUrl?: string | null;
      profileImageUrl?: string | null;
    });

const labRequiredFields: Array<[keyof PatientChart, string]> = [
  ["firstName", "first name"],
  ["lastName", "last name"],
  ["dateOfBirth", "date of birth"],
  ["gender", "gender"],
  ["phoneNumber", "phone number"],
  ["email", "email"],
  ["address", "address"],
  ["city", "city"],
  ["state", "state"],
  ["zipCode", "ZIP code"],
];

function isPatientRecord(value: unknown): value is CachedPatient {
  return (
    typeof value === "object" &&
    value !== null &&
    "id" in value &&
    typeof value.id === "string"
  );
}

function isPatientDataKey(key: readonly unknown[]): boolean {
  return key.some(
    (part) =>
      part === "detail" ||
      part === "table" ||
      part === "blacklist" ||
      part === "list" ||
      part === "duplicates" ||
      part === "duplicates-of-patient",
  );
}

function withRecomputedMissingLabFields<T extends CachedPatient>(
  patient: T,
): T {
  if (
    !("missingLabFields" in patient) ||
    !Array.isArray(patient.missingLabFields)
  ) {
    return patient;
  }

  return {
    ...patient,
    missingLabFields: labRequiredFields
      .filter(([field]) => !patient[field])
      .map(([, label]) => label),
  };
}

function mergePatientUpdate<T extends CachedPatient>(
  patient: T,
  update: PatientCacheUpdate,
): T {
  return withRecomputedMissingLabFields({
    ...patient,
    ...update,
    address:
      "address" in update ? (update.address ?? undefined) : patient.address,
    addressLineTwo:
      "addressLineTwo" in update
        ? (update.addressLineTwo ?? undefined)
        : patient.addressLineTwo,
    city: "city" in update ? (update.city ?? undefined) : patient.city,
    state: "state" in update ? (update.state ?? undefined) : patient.state,
    zipCode:
      "zipCode" in update ? (update.zipCode ?? undefined) : patient.zipCode,
    country:
      "country" in update ? (update.country ?? undefined) : patient.country,
  } as T);
}

function updatePatientValue(
  value: unknown,
  patientId: string,
  update: PatientCacheUpdate,
): { next: unknown; changed: boolean } {
  if (isPatientRecord(value)) {
    if (value.id !== patientId) return { next: value, changed: false };
    return { next: mergePatientUpdate(value, update), changed: true };
  }

  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map((item) => {
      const result = updatePatientValue(item, patientId, update);
      changed = changed || result.changed;
      return result.next;
    });
    return { next: changed ? next : value, changed };
  }

  if (
    typeof value === "object" &&
    value !== null &&
    "data" in value &&
    Array.isArray(value.data)
  ) {
    let changed = false;
    const data = value.data.map((item: unknown) => {
      const result = updatePatientValue(item, patientId, update);
      changed = changed || result.changed;
      return result.next;
    });
    return { next: changed ? { ...value, data } : value, changed };
  }

  return { next: value, changed: false };
}

export function updatePatientInCache(
  patientId: string,
  update: PatientCacheUpdate,
): Snapshot[] {
  const snapshots: Snapshot[] = [];
  const entries = queryClient.getQueriesData<unknown>({
    queryKey: patientQueryKeys.all,
  });

  for (const [key, value] of entries) {
    if (!Array.isArray(key) || !isPatientDataKey(key)) continue;
    const { next, changed } = updatePatientValue(value, patientId, update);
    if (!changed) continue;
    snapshots.push([key, value]);
    queryClient.setQueryData(key, next);
  }

  return snapshots;
}

/**
 * Optimistically prepend a patient into any cached patient table pages.
 * Returns snapshots for error reversion.
 */
export function addPatientToTables(optimistic: PatientSummary): Snapshot[] {
  const snapshots = queryClient.getQueriesData<Page<PatientTable> | undefined>({
    queryKey: patientQueryKeys.all,
  });

  const tableItem = summaryToTable(optimistic);

  snapshots.forEach(([key, value]) => {
    if (!Array.isArray(key) || !key.includes("table")) return;
    // A cohort's table lists only its members, and nothing here says the new
    // patient is one; leave that table to the refetch.
    const params = key[key.length - 1] as PatientTableRequest | undefined;
    if (params?.cohortId) return;
    const page = value as Page<PatientTable> | undefined;
    if (!page || !Array.isArray(page.data)) return;
    const exists = page.data.some((p) => p.id === tableItem.id);
    if (exists) return;
    queryClient.setQueryData<Page<PatientTable>>(key, {
      ...page,
      data: [tableItem, ...page.data],
    });
  });

  return snapshots as Snapshot[];
}

/**
 * Optimistically append tags to the given patients in any cached patient table
 * pages and patient detail entries. Skips tags already present on a patient so
 * the operation is idempotent. Returns snapshots for error reversion.
 */
export function addTagsToPatientsInCache(
  patientIds: string[],
  tags: PatientTag[],
): Snapshot[] {
  const snapshots: Snapshot[] = [];
  const patientIdSet = new Set(patientIds);

  const allEntries = queryClient.getQueriesData<unknown>({
    queryKey: patientQueryKeys.all,
  });

  for (const [key, value] of allEntries) {
    if (!Array.isArray(key)) continue;

    if (key.includes("table")) {
      const page = value as Page<PatientTable> | undefined;
      if (!page || !Array.isArray(page.data)) continue;

      // Honor tag-exclusion filters: if any of the new tags is in the page's
      // `tagIdsExclude`, the affected patients no longer match this query and
      // must be dropped rather than silently kept with appended tags.
      const params = key.find(
        (part): part is { tagIdsExclude?: string[] | null } =>
          typeof part === "object" && part !== null && "tagIdsExclude" in part,
      );
      const exclude = params?.tagIdsExclude ?? null;
      const newTagIds = tags.map((t) => t.id);
      const violatesExclude =
        Array.isArray(exclude) && exclude.some((id) => newTagIds.includes(id));

      let changed = false;
      const data = violatesExclude
        ? page.data.filter((patient) => !patientIdSet.has(patient.id))
        : page.data.map((patient) => {
            if (!patientIdSet.has(patient.id)) return patient;
            const existing = new Set((patient.tags ?? []).map((t) => t.id));
            const additions = tags.filter((t) => !existing.has(t.id));
            if (additions.length === 0) return patient;
            changed = true;
            return {
              ...patient,
              tags: [...(patient.tags ?? []), ...additions],
            };
          });
      if (violatesExclude && data.length !== page.data.length) changed = true;
      if (changed) {
        snapshots.push([key, page]);
        queryClient.setQueryData<Page<PatientTable>>(key, { ...page, data });
      }
      continue;
    }

    if (key.includes("detail")) {
      const chart = value as PatientChart | undefined;
      if (!chart || !patientIdSet.has(chart.id)) continue;
      const existing = new Set((chart.tags ?? []).map((t) => t.id));
      const additions = tags.filter((t) => !existing.has(t.id));
      if (additions.length === 0) continue;
      snapshots.push([key, chart]);
      queryClient.setQueryData<PatientChart>(key, {
        ...chart,
        tags: [...(chart.tags ?? []), ...additions],
      });
    }
  }

  return snapshots;
}

/**
 * Optimistically replace a single patient's tag set in any cached patient
 * detail entries. Returns snapshots for error reversion.
 *
 * Detail-only by design: the single-patient tag dialog reads `patient.tags`
 * from the detail query, and the patient table is filter-aware (tagIdsInclude /
 * tagIdsExclude) — reproducing replace semantics against those filters here is
 * error-prone, so table pages are left to refetch naturally.
 */
export function setPatientTagsInCache(
  patientId: string,
  tags: PatientTag[],
): Snapshot[] {
  const snapshots: Snapshot[] = [];
  const entries = queryClient.getQueriesData<unknown>({
    queryKey: patientQueryKeys.all,
  });

  for (const [key, value] of entries) {
    if (!Array.isArray(key) || !key.includes("detail")) continue;
    const chart = value as PatientChart | undefined;
    if (!chart || chart.id !== patientId) continue;
    snapshots.push([key, chart]);
    queryClient.setQueryData<PatientChart>(key, { ...chart, tags });
  }

  return snapshots;
}

/**
 * Optimistically remove patients from any cached patient table pages and
 * search results. Returns snapshots for error reversion.
 */
export function removePatientsFromTables(patientIds: string[]): Snapshot[] {
  const snapshots = queryClient.getQueriesData<Page<PatientTable> | undefined>({
    queryKey: patientQueryKeys.all,
  });

  queryClient
    .getQueriesData<PatientSummary[] | null>({
      queryKey: patientQueryKeys.searches(),
    })
    .forEach(([key, results]) => {
      if (!Array.isArray(results)) return;
      const filtered = results.filter((p) => !patientIds.includes(p.id));
      if (filtered.length === results.length) return;
      queryClient.setQueryData<PatientSummary[]>(key, filtered);
    });

  snapshots.forEach(([key, value]) => {
    if (!Array.isArray(key) || !key.includes("table")) return;
    const page = value as Page<PatientTable> | undefined;
    if (!page || !Array.isArray(page.data)) return;
    const filtered = page.data.filter((p) => !patientIds.includes(p.id));
    if (filtered.length === page.data.length) return; // No change
    queryClient.setQueryData<Page<PatientTable>>(key, {
      ...page,
      data: filtered,
    });
  });

  return snapshots as Snapshot[];
}

type FilterField =
  | "age"
  | "birthday"
  | "city"
  | "creditBalance"
  | "dateOfBirth"
  | "email"
  | "externalId"
  | "gender"
  | "goalWeight"
  | "hasEmail"
  | "hasPhoneNumber"
  | "hasProfilePicture"
  | "leadSource"
  | "locationIds"
  | "medicationCount"
  | "name"
  | "optedInToEmailMarketing"
  | "optedInToSmsMarketing"
  | "optedInToSocialMedia"
  | "phoneNumber"
  | "preferredName"
  | "preferredProviderId"
  | "pronouns"
  | "state"
  | "surchargeDisabled"
  | "zipCode";

function patientTableParams(
  key: readonly unknown[],
): PatientFetchParams | null {
  const tableIndex = key.indexOf("table");
  if (tableIndex === -1) return null;
  const params = key[tableIndex + 1];
  return typeof params === "object" && params !== null
    ? (params as PatientFetchParams)
    : null;
}

function patientIdsParams(key: readonly unknown[]): PatientFetchParams | null {
  const idsIndex = key.indexOf("ids");
  if (idsIndex === -1) return null;
  const params = key[idsIndex + 1];
  return typeof params === "object" && params !== null
    ? (params as PatientFetchParams)
    : null;
}

function hasValues(values: unknown[] | null | undefined): values is unknown[] {
  return Array.isArray(values) && values.length > 0;
}

function matchesIncludeExclude(
  value: string | null | undefined,
  include?: string[] | null,
  exclude?: string[] | null,
): boolean {
  if (hasValues(include) && (!value || !include.includes(value))) return false;
  if (hasValues(exclude) && value && exclude.includes(value)) return false;
  return true;
}

function matchesBooleanFilter(
  value: boolean,
  filter?: string[] | null,
): boolean {
  if (!hasValues(filter)) return true;
  const allowed = filter.map((item) => item === "true");
  return allowed.includes(value);
}

function matchesMarketingConsentFilter(
  value: boolean | null | undefined,
  filter?: string[] | null,
): boolean {
  if (!hasValues(filter)) return true;
  if (value === true) return filter.includes("true");
  if (value === false) return filter.includes("false");
  return filter.includes("not_set");
}

function matchesNumberRange(
  value: number,
  range?: CohortNumericRange | null,
): boolean {
  if (!Array.isArray(range)) return true;
  const [min, max] = range;
  if (min != null && value < min) return false;
  if (max != null && value > max) return false;
  return true;
}

function matchesTextFilter(
  value: string | null | undefined,
  filter: string | null | undefined,
): boolean {
  if (!filter) return true;
  return (value ?? "").toLowerCase().includes(filter.toLowerCase());
}

function matchesDateRange(
  value: string | null | undefined,
  range?: CohortDateRangeTuple | null,
): boolean {
  if (!Array.isArray(range)) return true;
  if (!value) return false;

  const [start, end] = range;
  if (start && value < start) return false;
  if (end && value > end) return false;
  return true;
}

function ageFromDateOfBirth(value: string | null | undefined): number | null {
  if (!value) return null;
  const dateOfBirth = new Date(`${value}T00:00:00`);
  if (Number.isNaN(dateOfBirth.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - dateOfBirth.getFullYear();
  const monthDifference = today.getMonth() - dateOfBirth.getMonth();
  if (
    monthDifference < 0 ||
    (monthDifference === 0 && today.getDate() < dateOfBirth.getDate())
  ) {
    age -= 1;
  }
  return age;
}

function matchesAgeRange(
  value: string | null | undefined,
  range?: CohortNumericRange | null,
): boolean {
  if (!Array.isArray(range)) return true;
  const age = ageFromDateOfBirth(value);
  return age !== null && matchesNumberRange(age, range);
}

function monthDayFromDate(value: string | null | undefined): number | null {
  if (!value) return null;
  const [, month, day] = value.split("-");
  if (!month || !day) return null;
  return Number(month) * 100 + Number(day);
}

function monthDayFromBirthdayFilter(value: string): number | null {
  if (!value.startsWith("--")) return null;
  const [month, day] = value.slice(2).split("-");
  if (!month || !day) return null;
  return Number(month) * 100 + Number(day);
}

function matchesBirthdayRange(
  value: string | null | undefined,
  range?: CohortMonthDayRange | null,
): boolean {
  if (!Array.isArray(range)) return true;
  const [start, end] = range;
  if (!start || !end) return true;

  const patientMonthDay = monthDayFromDate(value);
  const startMonthDay = monthDayFromBirthdayFilter(start);
  const endMonthDay = monthDayFromBirthdayFilter(end);
  if (
    patientMonthDay === null ||
    startMonthDay === null ||
    endMonthDay === null
  ) {
    return false;
  }

  if (startMonthDay <= endMonthDay) {
    return patientMonthDay >= startMonthDay && patientMonthDay <= endMonthDay;
  }
  return patientMonthDay >= startMonthDay || patientMonthDay <= endMonthDay;
}

function birthdayFilterNeedsServerEvaluation(
  params: PatientFetchParams,
  affectedFilters: Set<FilterField>,
): boolean {
  return (
    affectedFilters.has("birthday") &&
    params.birthday != null &&
    !Array.isArray(params.birthday)
  );
}

function tableSortUsesAffectedField(
  params: PatientFetchParams,
  affectedFilters: Set<FilterField>,
): boolean {
  if (!params.sortBy) return false;

  return (
    (affectedFilters.has("dateOfBirth") &&
      ["dateOfBirth", "date_of_birth"].includes(params.sortBy)) ||
    (affectedFilters.has("externalId") &&
      ["externalId", "external_id"].includes(params.sortBy)) ||
    (affectedFilters.has("name") &&
      ["firstName", "first_name", "lastName", "last_name", "name"].includes(
        params.sortBy,
      ))
  );
}

function paramsUseAffectedFilter(
  params: PatientFetchParams,
  affectedFilters: Set<FilterField>,
): boolean {
  return (
    (affectedFilters.has("age") && Array.isArray(params.age)) ||
    (affectedFilters.has("birthday") && Boolean(params.birthday)) ||
    (affectedFilters.has("city") && Boolean(params.city)) ||
    (affectedFilters.has("creditBalance") &&
      Array.isArray(params.creditBalance)) ||
    (affectedFilters.has("dateOfBirth") && Array.isArray(params.dateOfBirth)) ||
    (affectedFilters.has("email") && Boolean(params.email)) ||
    (affectedFilters.has("externalId") && Boolean(params.externalId)) ||
    (affectedFilters.has("gender") &&
      (hasValues(params.gender) || hasValues(params.genderExclude))) ||
    (affectedFilters.has("goalWeight") && Array.isArray(params.goalWeight)) ||
    (affectedFilters.has("hasEmail") && hasValues(params.hasEmail)) ||
    (affectedFilters.has("hasPhoneNumber") &&
      hasValues(params.hasPhoneNumber)) ||
    (affectedFilters.has("hasProfilePicture") &&
      hasValues(params.hasProfilePicture)) ||
    (affectedFilters.has("leadSource") &&
      (hasValues(params.leadSource) || hasValues(params.leadSourceExclude))) ||
    (affectedFilters.has("locationIds") &&
      (hasValues(params.locationIds) ||
        hasValues(params.locationIdsExclude))) ||
    (affectedFilters.has("medicationCount") &&
      Array.isArray(params.medicationCount)) ||
    (affectedFilters.has("name") && Boolean(params.name)) ||
    (affectedFilters.has("optedInToEmailMarketing") &&
      hasValues(params.optedInToEmailMarketing)) ||
    (affectedFilters.has("optedInToSmsMarketing") &&
      hasValues(params.optedInToSmsMarketing)) ||
    (affectedFilters.has("optedInToSocialMedia") &&
      hasValues(params.optedInToSocialMedia)) ||
    (affectedFilters.has("phoneNumber") && Boolean(params.phoneNumber)) ||
    (affectedFilters.has("preferredName") && Boolean(params.preferredName)) ||
    (affectedFilters.has("pronouns") && Boolean(params.pronouns)) ||
    (affectedFilters.has("state") && Boolean(params.state)) ||
    (affectedFilters.has("surchargeDisabled") &&
      hasValues(params.surchargeDisabled)) ||
    (affectedFilters.has("zipCode") && Boolean(params.zipCode)) ||
    (affectedFilters.has("preferredProviderId") &&
      (hasValues(params.preferredProviderId) ||
        hasValues(params.preferredProviderIdExclude)))
  );
}

type FilterPatient = {
  id: string;
  city?: string | null;
  creditBalance?: number | null;
  dateOfBirth?: string | null;
  email?: string | null;
  externalId?: string | null;
  firstName?: string | null;
  gender?: PatientTable["gender"] | null;
  goalWeight?: number | null;
  lastName?: string | null;
  leadSource?: string | null;
  medications?: PatientTable["medications"];
  preferredProviderId?: string | null;
  primaryLocationId?: string | null;
  profileImageUrl?: string | null;
  profilePictureUrl?: string | null;
  phoneNumber?: string | null;
  preferredName?: string | null;
  pronouns?: string | null;
  state?: string | null;
  surchargeDisabled?: boolean | null;
  optedInToEmailMarketing?: boolean | null;
  optedInToSmsMarketing?: boolean | null;
  optedInToSocialMedia?: boolean | null;
  zipCode?: string | null;
};

function patientMatchesAffectedFilters(
  patient: FilterPatient,
  params: PatientFetchParams,
  affectedFilters: Set<FilterField>,
): boolean {
  if (
    affectedFilters.has("city") &&
    !matchesTextFilter(patient.city, params.city)
  ) {
    return false;
  }

  if (
    affectedFilters.has("creditBalance") &&
    !matchesNumberRange(patient.creditBalance ?? 0, params.creditBalance)
  ) {
    return false;
  }

  if (
    affectedFilters.has("age") &&
    !matchesAgeRange(patient.dateOfBirth, params.age)
  ) {
    return false;
  }

  if (
    affectedFilters.has("birthday") &&
    !matchesBirthdayRange(patient.dateOfBirth, params.birthday)
  ) {
    return false;
  }

  if (
    affectedFilters.has("dateOfBirth") &&
    !matchesDateRange(patient.dateOfBirth, params.dateOfBirth)
  ) {
    return false;
  }

  if (
    affectedFilters.has("email") &&
    !matchesTextFilter(patient.email, params.email)
  ) {
    return false;
  }

  if (
    affectedFilters.has("externalId") &&
    params.externalId &&
    patient.externalId !== params.externalId
  ) {
    return false;
  }

  if (
    affectedFilters.has("gender") &&
    !matchesIncludeExclude(patient.gender, params.gender, params.genderExclude)
  ) {
    return false;
  }

  if (
    affectedFilters.has("goalWeight") &&
    !matchesNumberRange(patient.goalWeight ?? 0, params.goalWeight)
  ) {
    return false;
  }

  if (
    affectedFilters.has("hasEmail") &&
    !matchesBooleanFilter(Boolean(patient.email), params.hasEmail)
  ) {
    return false;
  }

  if (
    affectedFilters.has("hasPhoneNumber") &&
    !matchesBooleanFilter(Boolean(patient.phoneNumber), params.hasPhoneNumber)
  ) {
    return false;
  }

  if (
    affectedFilters.has("hasProfilePicture") &&
    !matchesBooleanFilter(
      Boolean(patient.profilePictureUrl || patient.profileImageUrl),
      params.hasProfilePicture,
    )
  ) {
    return false;
  }

  if (
    affectedFilters.has("name") &&
    !matchesTextFilter(
      `${patient.firstName ?? ""} ${patient.lastName ?? ""}`.trim(),
      params.name,
    )
  ) {
    return false;
  }

  if (
    affectedFilters.has("phoneNumber") &&
    !matchesTextFilter(patient.phoneNumber, params.phoneNumber)
  ) {
    return false;
  }

  if (
    affectedFilters.has("preferredName") &&
    !matchesTextFilter(patient.preferredName, params.preferredName)
  ) {
    return false;
  }

  if (
    affectedFilters.has("pronouns") &&
    !matchesTextFilter(patient.pronouns, params.pronouns)
  ) {
    return false;
  }

  if (
    affectedFilters.has("state") &&
    !matchesTextFilter(patient.state, params.state)
  ) {
    return false;
  }

  if (
    affectedFilters.has("surchargeDisabled") &&
    !matchesBooleanFilter(
      Boolean(patient.surchargeDisabled),
      params.surchargeDisabled,
    )
  ) {
    return false;
  }

  if (
    affectedFilters.has("zipCode") &&
    !matchesTextFilter(patient.zipCode, params.zipCode)
  ) {
    return false;
  }

  if (
    affectedFilters.has("optedInToEmailMarketing") &&
    !matchesMarketingConsentFilter(
      patient.optedInToEmailMarketing,
      params.optedInToEmailMarketing,
    )
  ) {
    return false;
  }

  if (
    affectedFilters.has("optedInToSmsMarketing") &&
    !matchesMarketingConsentFilter(
      patient.optedInToSmsMarketing,
      params.optedInToSmsMarketing,
    )
  ) {
    return false;
  }

  if (
    affectedFilters.has("optedInToSocialMedia") &&
    !matchesBooleanFilter(
      Boolean(patient.optedInToSocialMedia),
      params.optedInToSocialMedia,
    )
  ) {
    return false;
  }

  if (
    affectedFilters.has("leadSource") &&
    !matchesIncludeExclude(
      patient.leadSource,
      params.leadSource,
      params.leadSourceExclude,
    )
  ) {
    return false;
  }

  if (
    affectedFilters.has("locationIds") &&
    !matchesIncludeExclude(
      patient.primaryLocationId,
      params.locationIds,
      params.locationIdsExclude,
    )
  ) {
    return false;
  }

  if (
    affectedFilters.has("medicationCount") &&
    !matchesNumberRange(
      patient.medications?.length ?? 0,
      params.medicationCount,
    )
  ) {
    return false;
  }

  if (
    affectedFilters.has("preferredProviderId") &&
    !matchesIncludeExclude(
      patient.preferredProviderId,
      params.preferredProviderId,
      params.preferredProviderIdExclude,
    )
  ) {
    return false;
  }

  return true;
}

export function removePatientFromMismatchedFilteredCaches(
  patient: FilterPatient,
  affectedFilters: FilterField[],
): Snapshot[] {
  const affectedFilterSet = new Set(affectedFilters);
  const snapshots: Snapshot[] = [];
  const entries = queryClient.getQueriesData<unknown>({
    queryKey: patientQueryKeys.all,
  });

  for (const [key, value] of entries) {
    if (!Array.isArray(key)) continue;

    const tableParams = patientTableParams(key);
    if (
      tableParams &&
      (paramsUseAffectedFilter(tableParams, affectedFilterSet) ||
        tableSortUsesAffectedField(tableParams, affectedFilterSet))
    ) {
      if (tableSortUsesAffectedField(tableParams, affectedFilterSet)) {
        queryClient.invalidateQueries({ queryKey: key, exact: true });
        continue;
      }

      if (birthdayFilterNeedsServerEvaluation(tableParams, affectedFilterSet)) {
        queryClient.invalidateQueries({ queryKey: key, exact: true });
        continue;
      }

      const page = value as Page<PatientTable> | undefined;
      if (!page || !Array.isArray(page.data)) continue;

      const matchesFilters = patientMatchesAffectedFilters(
        patient,
        tableParams,
        affectedFilterSet,
      );
      const containsPatient = page.data.some((row) => row.id === patient.id);

      if (!matchesFilters && containsPatient) {
        const data = page.data.filter((row) => row.id !== patient.id);
        snapshots.push([key, page]);
        queryClient.setQueryData<Page<PatientTable>>(key, {
          ...page,
          data,
          total: Math.max(0, page.total - (page.data.length - data.length)),
        });
        queryClient.invalidateQueries({ queryKey: key, exact: true });
      } else if (matchesFilters && !containsPatient) {
        queryClient.invalidateQueries({ queryKey: key, exact: true });
      }
      continue;
    }

    const idsParams = patientIdsParams(key);
    if (idsParams && paramsUseAffectedFilter(idsParams, affectedFilterSet)) {
      if (birthdayFilterNeedsServerEvaluation(idsParams, affectedFilterSet)) {
        queryClient.invalidateQueries({ queryKey: key, exact: true });
        continue;
      }

      const ids = value as string[] | undefined;
      if (!Array.isArray(ids)) continue;

      const matchesFilters = patientMatchesAffectedFilters(
        patient,
        idsParams,
        affectedFilterSet,
      );
      const containsPatient = ids.includes(patient.id);

      if (!matchesFilters && containsPatient) {
        snapshots.push([key, ids]);
        queryClient.setQueryData<string[]>(
          key,
          ids.filter((id) => id !== patient.id),
        );
      } else if (matchesFilters && !containsPatient) {
        queryClient.invalidateQueries({ queryKey: key, exact: true });
      }
    }
  }

  return snapshots;
}
